import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback,  useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useSAL6010_R01Store } from "./Store_SAL6010_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { settingSAL6010_R01CellClasses } from "src/utils/grid/GridCellStylesContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL6010_R01DetailDto, SAL6010_R01DetailDto } from "./Dto_SAL6010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { showSuccess, showWarning, showError } from "src/utils/toast/toastUtil";
import { useExcelPaste } from "src/hooks/useExcelPaste";
import { SAL6010_EXCEL_CONFIG } from "src/configs/excelPasteConfigs";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { useSAL6010_R01Modal } from "./hooks/useSAL6010_R01Modal";
import { useSAL6010_R01CellChange } from "./hooks/SAL6010_R01CellChange";

const COLUMN_STATE_KEY = 'SAL6010_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL6010_R01DetailDto> | null>;
	defaultSAL6010_R01DetailRow: SAL6010_R01DetailDto;
}

/**
 * @description
 * SAL6010 견적서 관리 화면의 상세 품목 테이블 컴포넌트입니다.
 * AG Grid를 사용하여 견적에 포함된 품목 목록을 표시하고 관리합니다.
 *
 * @features
 * - 품목 목록 조회 및 표시
 * - 행 추가(+), 삭제(-) 기능
 * - 품목 코드, 수량, 단가 등 직접 편집
 * - 품목 코드 입력 시 품명, 규격 등 자동 완성 (useDetailTableWithItemSearch)
 * - 엑셀 데이터 복사/붙여넣기 (useExcelPaste)
 * - 데이터 변경 시 히스토리 관리 및 되돌리기 (Ctrl+Z)
 * - 컬럼 너비/순서/숨김 상태 개인화 및 저장
 * - 하단에 합계 정보 (총 건수, 수량, 금액) 표시
 */
export const DetailTable_SAL6010_R01 = (props: Props) => {

	const { gridRef, defaultSAL6010_R01DetailRow } = props;

	/**
	 * Zustand 스토어에서 상태 및 상태 관리 함수들을 가져옵니다.
	 * - SAL6010_R01DetailList: 상세 품목 목록 데이터
	 * - selectedSAL6010_R01DetailList: 사용자가 선택한 행의 목록
	 * - deletedSAL6010_R01DetailList: 삭제 표시된 행의 목록
	 * - originalSAL6010_R01DetailList: 조회된 원본 데이터 목록 (변경 여부 비교용)
	 * - detailColumnState: 그리드 컬럼 상태 (너비, 순서 등)
	 */
	const { 
			SAL6010_R01DetailList, setSAL6010_R01DetailList, 
			selectedSAL6010_R01DetailList, setSelectedSAL6010_R01DetailList, removeSelectedSAL6010_R01DetailList, addSelectedSAL6010_R01DetailList, initSelectedSAL6010_R01DetailList,
			deletedSAL6010_R01DetailList, setDeletedSAL6010_R01DetailList, addDeletedSAL6010_R01DetailList, removeDeletedSAL6010_R01DetailList,
			insertSAL6010_R01DetailList, updateSAL6010_R01DetailList, removeSAL6010_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalSAL6010_R01DetailList, setOriginalSAL6010_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = useSAL6010_R01Store();
	const { setCodeSearchModalProps } = useSAL6010_R01Modal();
	const [clickable, setClickable] = useState(true);

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 그리드 전체를 감싸는 div의 ref (붙여넣기 이벤트 감지용)

	/**
	 * 공통 그리드 설정 훅 (useGridSettingContent)
	 * @description 그리드 행의 기본 설정을 관리합니다.
	 * - getRowId: 각 행의 고유 ID를 생성 (valueIndex 사용)
	 * - createRowClassRules: 행의 상태(선택, 삭제, 수정 등)에 따른 CSS 클래스 규칙 생성
	 * - paddedGridData: 그리드에 항상 최소한의 빈 행을 유지하기 위한 데이터
	 * - renderIndexCell: 행 번호 및 +/- 버튼을 렌더링하는 함수
	 */
	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL6010_R01DetailList,
		defaultGridData: defaultSAL6010_R01DetailRow,
		selectedRow: selectedSAL6010_R01DetailList,
		originalGridData: originalSAL6010_R01DetailList,
		updateRow: (index: number, data: any) => updateSAL6010_R01DetailList(index, data),
	});

	/**
	 * 그리드 컬럼의 한글 헤더명을 정의합니다. (컬럼 상태 복원 시 사용)
	 */
	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '모델번호',
		estimateUnit: '단위',
		estimateQty: '수량',
		estimatePrice: '단가',
		dcAmt: '할인액',
		itemAmt: '금액',
	}

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'modelNo', 'estimateUnit', 'itemAmt'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd'],
		pointerCells: ['estimateQty', 'estimatePrice', 'dcAmt'],
		insertedPointerCells: ['itemCd', 'itemNm', 'itemSpec', 'estimateUnit', 'modelNo'],
		commonPointerCells: [''],
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SAL6010_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedSAL6010_R01DetailList,
			deletedList: deletedSAL6010_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedSAL6010_R01DetailList, deletedSAL6010_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo', 'estimateUnit', 'estimateQty', 'estimatePrice', 'dcRate', 'dcAmt'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: SAL6010_R01DetailList,
			rowData: SAL6010_R01DetailList[rowIndex],
			insertRow: insertSAL6010_R01DetailList,
			defaultRowData: defaultSAL6010_R01DetailRow,
			setClickable
		});
	}, [SAL6010_R01DetailList, insertSAL6010_R01DetailList, defaultSAL6010_R01DetailRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 	
		createGridDropDownCommitAfter<SAL6010_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	/**
	 * AG Grid의 기본 컬럼 정의(ColDef) 배열입니다.
	 * useMemo를 사용하여 불필요한 리렌더링을 방지합니다.
	 */
	const baseColumnDefs: ColDef<SAL6010_R01DetailDto>[] = useMemo(() => [
		/** 행 번호, +/- 버튼 컬럼 */
		...getDefaultRowIndexColumnConfig(
			(params: SAL6010_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: SAL6010_R01DetailDto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedSAL6010_R01DetailList,
						selectedList: selectedSAL6010_R01DetailList,
						dataList: SAL6010_R01DetailList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SAL6010_R01DetailList, 
					rowData,
					setClickable, 
					removeSAL6010_R01DetailList, 
					(valueIndex) => addDeletedSAL6010_R01DetailList([valueIndex.toString()])
				),
			)
		),
		/** 품목코드 컬럼 */
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,	
			editable: ((params) => params.data?.inserted === true),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			editable: false,
		},
		{
			headerName: '모델번호', colId: 'modelNo', field: 'modelNo', width: 130,
			editable: false,
		},
		{
			headerName: '단위', colId: 'estimateUnit', field: 'estimateUnit', width: 60,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateSAL6010_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '수량', colId: 'estimateQty', field: 'estimateQty', width: 60,
			editable: (params) => SAL6010_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},

		{
			headerName: '단가', colId: 'estimatePrice', field: 'estimatePrice', width: 65,
			editable: (params) => SAL6010_R01DetailList[params.node.rowIndex!] !== undefined,
			valueGetter: (p: any) => p.data?.estimatePrice ?? p.data?.itemPrice ?? p.data?.itemCost ?? 0,
			valueSetter: (p: any) => { p.data.estimatePrice = Number(p.newValue) || 0; return true; },
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => (params.value ?? 0).toLocaleString(),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '할인금액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			editable: (params) => SAL6010_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 96,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// ...IndexParameterCell(),
	], [deletedSAL6010_R01DetailList, SAL6010_R01DetailList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	/**
	 * 로컬 스토리지에 저장된 사용자별 컬럼 상태(너비, 순서 등)를 적용한 최종 컬럼 정의입니다.
	 */
	const columnDefs: ColDef<SAL6010_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<SAL6010_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);
	

	/**
	 * 그리드 행 클릭 시 선택/해제 로직을 처리하는 핸들러입니다. (useRowSelectionHandler)
	 * Ctrl, Shift 키를 이용한 다중 선택을 지원합니다.
	 */
	const handleRowClick = useRowSelectionHandler({
		dataList: SAL6010_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedSAL6010_R01DetailList,
		deletedList: deletedSAL6010_R01DetailList,
		addSelected: addSelectedSAL6010_R01DetailList,
		removeSelected: removeSelectedSAL6010_R01DetailList,
		clickable, setClickable
	});

	// === 상세 테이블 공통 핸들러 로직 ===
	const { searchExactItemCode, searchExactCode } = useCodeSearch({});

	const detailTableHandlers = useSAL6010_R01CellChange({
		SAL6010_R01DetailList,
		originalSAL6010_R01DetailList,
		updateSAL6010_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
	});

	
	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<SAL6010_R01DetailDto>({
		detailList: SAL6010_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',		// 품목코드
					itemNm: 'itemNm',		// 품명
					itemSpec: 'itemSpec',	// 규격
					modelNo: 'modelNo',		// 품번
					itemUnit: 'estimateUnit',		// 단위
					itemCost: 'estimatePrice'	// 단가
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "",
					estimateUnit: "", estimatePrice: 0, dcAmt: 0, itemAmt: 0
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'orderQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});

	// 행 드래그 앤 드롭이 진행 중인지 여부를 판별하는 ref
	const isRowDraggingRef = useRef(false);

	/**
	 * 그리드 정렬이 완료된 후 호출되는 콜백 함수입니다.
	 * @description 행 순서를 유지하기 위해 valueIndex를 재정렬합니다.
	 * 행 드래그 중에는 자동 정렬을 방지합니다.
	 */
	const handlePostSort = useCallback((params: PostSortRowsParams<SAL6010_R01DetailDto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SAL6010_R01DetailList, originalSAL6010_R01DetailList, setSAL6010_R01DetailList);
	}, [SAL6010_R01DetailList, originalSAL6010_R01DetailList, ]);


	/**
	 * GridContextWrapper의 데이터 히스토리 저장 함수에 접근하기 위한 ref
	 */
	const saveHistoryRef = useRef<((data: SAL6010_R01DetailDto[]) => void) | null>(null);

	/**
	 * 이 모듈을 위한 엑셀 붙여넣기 설정을 생성합니다.
	 * @description 공통 엑셀 붙여넣기 훅(useExcelPaste)에 전달될 설정 객체입니다.
	 * 컬럼 매핑, 자동 완성 함수 등을 정의합니다.
	 */
	const excelConfig = useMemo(() => ({
		...SAL6010_EXCEL_CONFIG,
		autoCompleteFields: {
			...SAL6010_EXCEL_CONFIG.autoCompleteFields!,
			searchFunction: (code: string) => searchExactCode('item', code)
		}
	}), [searchExactCode]);

	/**
	 * 공통 엑셀 붙여넣기 훅 (useExcelPaste)
	 * @description 클립보드의 데이터를 파싱하여 그리드에 붙여넣는 로직을 처리합니다.
	 * @returns handleExcelPaste: GridContextWrapper로 전달될 콜백 함수
	 */
	const { handleExcelPaste: handleExcelPasteCallback } = useExcelPaste(
		excelConfig,
		SAL6010_R01DetailList,
		setSAL6010_R01DetailList,
		saveHistoryRef,
		gridRef
	);


	/**
	 * 그리드 하단에 고정될 합계 행의 데이터를 계산합니다.
	 * useMemo를 사용하여 상세 목록이 변경될 때만 재계산합니다.
	 */
	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = SAL6010_R01DetailList
			.filter(item => !deletedSAL6010_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum, item) => sum + (Number(item.estimateQty) || 0), 0);
		const totalAmt : number = SAL6010_R01DetailList
			.filter(item => !deletedSAL6010_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum, item) => sum + (Number(item.itemAmt) || 0), 0);

		return [{
			itemCd: SAL6010_R01DetailList.length.toLocaleString() + "건",
			estimateQty: totalQty.toLocaleString(),
			itemAmt: totalAmt.toLocaleString()
		}];	  
	}, [SAL6010_R01DetailList, deletedSAL6010_R01DetailList]);


	return (
		/**
		 * GridContextWrapper
		 * @description 그리드의 공통 컨텍스트(우클릭 메뉴, 엑셀 붙여넣기, 데이터 히스토리 등)를 제공하는 래퍼 컴포넌트입니다.
		 */
		<GridContextWrapper<SAL6010_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			// GridContextWrapper에서 히스토리 관리 및 되돌리기 처리
			// 여기서는 단순히 데이터 업데이트만 수행
			setPaddedGridData={(data) => { setSAL6010_R01DetailList(data); }}
			initSelected={initSelectedSAL6010_R01DetailList}
			defaultRowData={defaultSAL6010_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
			// 엑셀 붙여넣기 기능 활성화
			enableExcelPaste={true}
			onExcelPaste={handleExcelPasteCallback}
			// 데이터 히스토리 기능 활성화 (엑셀 붙여넣기 되돌리기 등)
			enableDataHistory={true}
			historyLimit={15}
			bulkChangeThreshold={2}
			onHistorySave={(saveHistoryFn) => {
				// GridContextWrapper의 히스토리 저장 함수를 ref에 저장
				saveHistoryRef.current = saveHistoryFn;
			}}
		>

			{/**
			 * Grid_Excel
			 * @description 실제 AG Grid를 렌더링하는 컴포넌트입니다.
			 * +- 버튼 기능이 포함된 그리드입니다.
			 */}
			<Grid_Excel<SAL6010_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId} // +- 버튼 이벤트 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				//  새로운 훅 구조와 호환
				disableBuiltinCellSelection={true} // 내장 셀 선택 비활성화 (새로운 훅 사용)
				columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
				rowClassRules={createRowClassRules<SAL6010_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL6010_R01DetailList,
					deletedList: deletedSAL6010_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
                onCellClicked={cellEventHandlers.handleCellClick}
                onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}       // 셀 값 변경 (금액 계산)
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped} // 편집 종료
				pinnedBottomRowData={pinnedBottomRowData}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: SAL6010_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setSAL6010_R01DetailList(reordered);
					isRowDraggingRef.current = false;
                }}
				

				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort}		
			/>
		</GridContextWrapper>
	)
}
